import React from 'react';
import PropTypes from 'prop-types';
import {
  View, Text, StyleSheet, ActivityIndicator, TouchableOpacity,
} from 'react-native';

import Theme from '../theme';
import Sizes from '../sizes';
import { scaleText, defaultScale } from '../utils/scaling';

const Button = ({
  title,
  subtitle,
  onPress,
  fontColor,
  fontSize,
  subtitleFontSize = 12,
  containerStyle,
  isLoading = false,
  disabled = false,
  buttonType = ButtonTypes.NORMAL,
  icon = null,
  overrideFontStyles,
  overrideSubtitleFontStyles,
  size = ButtonSize.NORMAL,
  theme = 'light',
  contentContainerStyle = styles.contentContainerStyle,
}) => {
  const mappedButtonTypeMap = {
    NORMAL: {
      color: Theme.PRIMARY === Theme.BLACK
        ? Theme.WHITE
        : Theme.CHARCOAL,
      borderColor: Theme.PRIMARY,
      backgroundColor: Theme.PRIMARY,
    },
    OUTLINE: {
      color: Theme.PRIMARY === Theme.BLACK
        ? Theme.WHITE
        : Theme.PRIMARY,
      borderColor: Theme.PRIMARY,
      backgroundColor: 'transparent',
    },
    TRANSPARENT: {
      color: Theme.PRIMARY,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
    },
    DISABLED: {
      light: {
        color: Theme.GREY_MEDIUM_LIGHT,
        borderColor: Theme.GREY_LIGHT,
        backgroundColor: Theme.GREY_LIGHT,
      },
      dark: {
        color: Theme.GREY_MEDIUM_LIGHT,
        borderColor: Theme.GREY_MEDIUM_LIGHT,
        backgroundColor: Theme.GREY_MEDIUM_LIGHT,
      },
    },
  };

  const resolvedButtonTypeStyle = mappedButtonTypeMap[buttonType];

  const borderColor = () => {
    if (disabled || isLoading) {
      if (buttonType === ButtonTypes.TRANSPARENT) {
        return resolvedButtonTypeStyle.borderColor;
      }
      return mappedButtonTypeMap.DISABLED[theme].borderColor;
    }
    return resolvedButtonTypeStyle.borderColor;
  };

  const backgroundColor = () => {
    if (disabled || isLoading) {
      if (buttonType === ButtonTypes.TRANSPARENT) {
        return resolvedButtonTypeStyle.backgroundColor;
      }
      return mappedButtonTypeMap.DISABLED[theme].backgroundColor;
    }
    return resolvedButtonTypeStyle.backgroundColor;
  };

  const getFontColor = () => {
    if (disabled) {
      return mappedButtonTypeMap.DISABLED[theme].color;
    }
    if (fontColor) {
      return fontColor;
    }
    return resolvedButtonTypeStyle.color;
  };

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled || isLoading}
      style={[styles.container, {
        height: size.height,
        borderColor: borderColor(),
        backgroundColor: backgroundColor(),
      }, containerStyle]}
    >
      {isLoading
        ? (
          <ActivityIndicator
            size='small'
            animating={isLoading}
            color={Theme.PRIMARY}
          />
        )
        : (
          <View style={[contentContainerStyle, {
            justifyContent: 'center',
            alignItems: 'center',
          }]}
          >
            {icon}
            <View style={{ flexDirection: 'column' }}>
              <Text style={[
                styles.text,
                {
                  fontSize: fontSize || size.fontSize,
                  color: getFontColor(),
                },
                overrideFontStyles]}
              >
                {title}
              </Text>
              {subtitle
              && (
              <Text style={[
                styles.text,
                {
                  fontSize: subtitleFontSize,
                  color: getFontColor(),
                },
                overrideSubtitleFontStyles]}
              >
                {subtitle}
              </Text>
              )}
            </View>
          </View>
        )}
    </TouchableOpacity>
  );
};

Button.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.element,
  ]).isRequired,
  onPress: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  fontSize: PropTypes.number,
  fontColor: PropTypes.string,
  buttonType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  containerStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  icon: PropTypes.element,
  subtitle: PropTypes.string,
  subtitleFontSize: PropTypes.number,
  overrideSubtitleFontStyles: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  overrideFontStyles: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
};

const styles = StyleSheet.create({
  container: {
    height: defaultScale(Sizes.ButtonHeight),
    borderWidth: 1,
    borderRadius: 2,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  text: {
    ...Theme.textStyle.bodyCopyBold,
    textAlign: 'center',
  },
  contentContainerStyle: {
    flexDirection: 'row',
  },
});

export const ButtonSize = {
  NORMAL: {
    height: defaultScale(Sizes.ButtonHeight),
    fontSize: scaleText(16),
  },
  MEDIUM: {
    height: defaultScale(Sizes.ButtonMedium),
    fontSize: scaleText(16),
  },
  LARGE: {
    height: defaultScale(Sizes.ButtonLarge),
    fontSize: scaleText(20),
  },
};

export const ButtonTypes = {
  NORMAL: 'NORMAL',
  OUTLINE: 'OUTLINE',
  TRANSPARENT: 'TRANSPARENT',
  DISABLED: 'DISABLED',
};

export default Button;
